export function showAddRemoveSection(status: string): boolean {
  if (!status) return false;
  const allowedStatuses = [
    "vendor_information_requested",
    "partially_submitted",
    "under_product_developer_review",
    "under_internal_review",
  ];
  return allowedStatuses.includes(status);
}

/** Extract a single ID from a multi-select value (string, option object, or { id, value }). */
export function extractActiveProjectMultiSelectId(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const nested = o.option as Record<string, unknown> | undefined;
    const found = nested?.value ?? o.value ?? o.id ?? "";
    return String(found).trim();
  }
  return String(value).trim();
}

/** Parse multi-select IDs from API/form; comma-separated with legacy pipe fallback. */
export function parseActiveProjectMultiSelectIds(value: unknown): string[] {
  if (value == null || value === "") return [];
  if (Array.isArray(value)) {
    return value.map(extractActiveProjectMultiSelectId).filter(Boolean);
  }
  if (typeof value === "object") {
    const id = extractActiveProjectMultiSelectId(value);
    return id ? [id] : [];
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];
    const separator = trimmed.includes("|") ? "|" : ",";
    return trimmed.split(separator).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** Format multi-select IDs for API payload (default comma-separated). */
export function formatActiveProjectMultiSelectIdsForSave(
  value: unknown,
  separator: "," | "|" = ","
): string {
  return parseActiveProjectMultiSelectIds(value).join(separator);
}

/** Shared parse logic for JSON/comma/pipe multi-select save values. */
function normalizeActiveProjectMultiSelectIdsFromSaveValue(
  value: unknown
): string[] {
  if (value == null || value === "") return [];

  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "string"
          ? item.replace(/^\["|"\]$/g, "").replace(/^"|"$/g, "")
          : extractActiveProjectMultiSelectId(item)
      )
      .filter(Boolean);
  }

  if (typeof value === "object") {
    const id = extractActiveProjectMultiSelectId(value);
    return id ? [id] : [];
  }

  if (typeof value !== "string") return [];

  const trimmed = value.trim();

  if (!trimmed || trimmed === "[]") return [];

  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);

      if (Array.isArray(parsed)) {
        return parsed
          .map((item) =>
            typeof item === "string"
              ? item.replace(/^\["|"\]$/g, "").replace(/^"|"$/g, "")
              : extractActiveProjectMultiSelectId(item)
          )
          .filter(Boolean);
      }

      if (parsed && typeof parsed === "object") {
        const id = extractActiveProjectMultiSelectId(parsed);
        return id ? [id] : [];
      }
    } catch {
      // fall through
    }
  }

  return [];
}

/**
 * Get multi-select id array from a save/API string.
 * Supports JSON (`'["1","2"]'`), comma-separated (`"1,2"`), and pipe-separated (`"1|2"`) values.
 */
export function getArrayFromJsonString(value: unknown): string[] {
  return normalizeActiveProjectMultiSelectIdsFromSaveValue(value);
}

/** Format multi-select IDs as a JSON.stringify string array for API payload. */
export function getJsonStringFromArray(value: unknown): string {
  return JSON.stringify(normalizeActiveProjectMultiSelectIdsFromSaveValue(value));
}

/** True when a string looks like JSON.stringify output (array, object, or quoted string). */
export function isJsonStringifiedValue(value: unknown): boolean {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  const first = trimmed[0];
  return first === "[" || first === "{" || first === '"';
}

/**
 * Format a JSON.stringify value for display (e.g. audit log old/new values).
 * Only transforms valid JSON.stringify shapes; plain strings are returned unchanged.
 */
export function formatJsonStringifiedValueForDisplay(value: unknown): string {
  if (value == null) return "";
  const str = String(value);
  if (!isJsonStringifiedValue(str)) return str;

  const trimmed = str.trim();

  if (trimmed.startsWith("[")) {
    try {
      JSON.parse(trimmed);
      return getArrayFromJsonString(trimmed).join(", ");
    } catch {
      return str;
    }
  }

  try {
    const parsed = JSON.parse(trimmed);

    if (parsed !== null && typeof parsed === "object") {
      return Object.values(parsed)
        .map((item) => (item == null ? "" : String(item)))
        .filter((item) => item.length > 0)
        .join(", ");
    }

    return parsed == null ? "" : String(parsed);
  } catch {
    return str;
  }
}

